import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { AppLayout } from "@/components/ui-system";
import EdgeAIAssistant from "@/components/EdgeAIAssistant";
import { can } from "@/lib/permissions";
import {
  LayoutDashboard, Package, PackageCheck, Calendar, MapPin, MapPinned, Users, Truck as TruckIcon,
  Upload, Bell, FileSpreadsheet, BookOpen, Calculator, Navigation, FileText, Wallet, Boxes, ShieldCheck, BadgeCheck, SlidersHorizontal, Sparkles, RotateCcw, Banknote, PackageX, LifeBuoy, Store, Scale, Zap, Webhook, Globe,
} from "lucide-react";

const SellerLayout = ({ children, user, onLogout }) => {
  const navigate = useNavigate();
  const [walletBalance, setWalletBalance] = useState(null);
  const [sellerInfo, setSellerInfo] = useState(null);
  const [supportOpen, setSupportOpen] = useState(0);

  const canViewWallet = can(user, "wallet:view");
  const canViewSupport = can(user, "support:view");
  useEffect(() => {
    if (canViewWallet) {
      axios.get(`${API}/wallet/balance`).then((r) => setWalletBalance(r.data.balance ?? 0)).catch(() => {});
    }
    axios.get(`${API}/seller/profile`).then((r) => r.data.seller_info && setSellerInfo(r.data.seller_info)).catch(() => {});
  }, [canViewWallet]);

  useEffect(() => {
    if (!canViewSupport) return;
    const fetchOpen = () => axios.get(`${API}/support/open-count`).then((r) => setSupportOpen(r.data.open ?? 0)).catch(() => {});
    fetchOpen();
    const interval = setInterval(fetchOpen, 120000);
    return () => clearInterval(interval);
  }, [canViewSupport]);

  const brandName = sellerInfo?.brand_name || "";
  const brandLogoUrl = sellerInfo?.brand_logo_url;

  // Permission keys per docs/RBAC_MIGRATION_MAP.md "Frontend contract".
  // Gating here is cosmetic — the backend enforces the same permissions.
  const baseMenu = [
    { section: "B2C Last Mile" },
    { name: "B2C Dashboard", path: "/seller/b2c", icon: LayoutDashboard, testId: "nav-b2c-dashboard", end: true, permission: "b2c.dashboard:view" },
    { name: "B2C Orders", path: "/seller/b2c/orders", icon: PackageCheck, testId: "nav-b2c-orders", permission: "b2c.orders:view" },
    { name: "Sales Channels", path: "/seller/b2c/channels", icon: Store, testId: "nav-b2c-channels", permission: "b2c.channels:view" },
    { name: "Returns & Exchange", path: "/seller/b2c/returns", icon: RotateCcw, testId: "nav-b2c-returns", permission: "b2c.returns:view" },
    { name: "COD Reconciliation", path: "/seller/b2c/cod", icon: Banknote, testId: "nav-b2c-cod", permission: "b2c.cod:view" },
    { name: "Early COD", path: "/seller/b2c/early-cod", icon: Zap, testId: "nav-b2c-early-cod", permission: "b2c.early_cod:view" },
    { name: "NDR", path: "/seller/b2c/ndr", icon: PackageX, testId: "nav-b2c-ndr", permission: "b2c.ndr:view" },
    { name: "Weight Discrepancy", path: "/seller/b2c/weight-discrepancies", icon: Scale, testId: "nav-b2c-weight", permission: "b2c.weight_discrepancy:view" },
    { name: "Rate Calculator", path: "/seller/b2c/rate-calculator", icon: Calculator, testId: "nav-b2c-rate-calc", permission: "b2c.rate_calculator:use" },
    { name: "KYC Verification", path: "/seller/b2c/kyc", icon: BadgeCheck, testId: "nav-b2c-kyc", permission: "kyc:submit" },
    { name: "Plan", path: "/seller/b2c/plan", icon: Sparkles, testId: "nav-b2c-plan", permission: "b2c.plan:manage" },
    { name: "Courier Setting", path: "/seller/courier-selection-criteria", icon: SlidersHorizontal, testId: "nav-courier-criteria", permission: "b2c.courier_criteria:manage" },
    { name: "Tracking Webhook", path: "/seller/b2c/webhook", icon: Webhook, testId: "nav-b2c-webhook", permission: "b2c.orders:view" },
    { section: "B2B Last Mile" },
    { name: "B2B Dashboard", path: "/seller", icon: LayoutDashboard, testId: "nav-dashboard", end: true, permission: "dashboard:view" },
    { name: "B2B Orders", path: "/seller/orders", icon: Boxes, testId: "nav-orders", permission: "orders:view" },
    { name: "Bulk Orders", path: "/seller/bulk-orders", icon: Upload, testId: "nav-bulk-orders", permission: "bulk_orders:view" },
    { name: "Appointments", path: "/seller/appointments", icon: Calendar, testId: "nav-appointments", permission: "appointments:view" },
    { name: "Pickup Requests", path: "/seller/pickup-requests", icon: TruckIcon, testId: "nav-pickup-requests", permission: "pickup_requests:view" },
    { name: "Drop Locations", path: "/seller/drop-locations", icon: MapPinned, testId: "nav-drop-locations", permission: "locations:view" },
    { name: "B2B Rate Card", path: "/seller/b2b-rate-card", icon: FileText, testId: "nav-b2b-rate-card" },
    { section: "Tools" },
    { name: "TAT Calculator", path: "/seller/tat-calculator", icon: Calculator, testId: "nav-tat-calculator", permission: "tools:use" },
    { name: "Freight Estimator", path: "/seller/freight-estimator", icon: Package, testId: "nav-freight-estimator", permission: "tools:use" },
    { name: "Route Planning", path: "/seller/route-planning", icon: Navigation, testId: "nav-route-planning", permission: "tools:use" },
    { name: "Zone Definition", path: "/seller/zone-definition", icon: Globe, testId: "nav-zone-definition" },
    { section: "Account" },
    { name: "Pickup Locations", path: "/seller/pickup-locations", icon: MapPin, testId: "nav-pickup-locations", permission: "locations:view" },
    { name: "Reports", path: "/seller/reports", icon: FileSpreadsheet, testId: "nav-reports", permission: "reports:view" },
    { name: "Invoices", path: "/seller/invoices", icon: FileText, testId: "nav-invoices", permission: "invoices:view" },
    { name: "Support", path: "/seller/support", icon: LifeBuoy, testId: "nav-support", permission: "support:view" },
    { name: "Notification Setup", path: "/seller/notification-setup", icon: Bell, testId: "nav-notification-setup", permission: "notifications:manage" },
    { name: "Users", path: "/seller/users", icon: Users, testId: "nav-users", permission: "users:view" },
    // Team Roles is hard-locked to seller_admin (never grantable).
    ...(user?.role === "seller_admin"
      ? [{ name: "Team Roles", path: "/seller/roles", icon: ShieldCheck, testId: "nav-team-roles" }]
      : []),
  ];
  // Filter by permission, then drop section headers left with no visible items.
  const visibleMenu = baseMenu.filter((item) =>
    item.section || !item.permission || can(user, item.permission)
  );
  const nav = visibleMenu.filter((item, idx) =>
    !item.section || (visibleMenu[idx + 1] && !visibleMenu[idx + 1].section)
  );

  const logo = brandLogoUrl
    ? <img src={brandLogoUrl} alt={brandName} className="w-8 h-8 rounded-lg object-cover" />
    : <TruckIcon className="w-5 h-5 text-white" />;

  const walletButton = canViewWallet && (
    <button
      data-testid="wallet-btn"
      onClick={() => navigate("/seller/wallet")}
      className="w-full flex items-center gap-3 px-4 py-3 mb-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
    >
      <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
        <Wallet className="w-5 h-5 text-white" />
      </div>
      <div className="flex-1 min-w-0 text-left">
        <p className="text-xs text-slate-500">Wallet Balance</p>
        <p className={`text-sm font-bold ${walletBalance !== null && walletBalance < 0 ? "text-red-600" : "text-slate-800"}`}>
          {walletBalance !== null ? `₹${walletBalance.toLocaleString("en-IN")}` : "..."}
        </p>
      </div>
    </button>
  );

  const topBar = (
    <div className="hidden md:flex items-center justify-end gap-2 px-8 py-2 bg-white border-b border-slate-100">
      {can(user, "support:view") && (
        <button
          onClick={() => navigate("/seller/support")}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold text-teal-700 bg-teal-50 hover:bg-teal-100 rounded-md transition-colors"
          data-testid="support-top-btn"
          title="Get help — raise or track a support ticket"
        >
          <LifeBuoy className="w-4 h-4" /> Support
          {supportOpen > 0 && (
            <span className="ml-0.5 min-w-[18px] h-[18px] px-1 inline-flex items-center justify-center rounded-full bg-teal-600 text-white text-xs font-bold" data-testid="support-open-count">
              {supportOpen > 99 ? "99+" : supportOpen}
            </span>
          )}
        </button>
      )}
      <button
        onClick={() => navigate("/seller/docs")}
        className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-600 hover:text-teal-600 hover:bg-teal-50 rounded-md transition-colors"
        data-testid="docs-top-btn"
      >
        <BookOpen className="w-4 h-4" /> Documentation
      </button>
    </div>
  );

  return (
    <AppLayout
      variant="sidebar"
      accent="teal"
      navKey="seller"
      brand={{ name: "EdgeLMS", subtitle: brandName, logo }}
      nav={nav}
      user={user}
      onLogout={onLogout}
      profilePath="/seller/profile"
      topBar={topBar}
      footerExtra={walletButton}
      assistant={<EdgeAIAssistant />}
    >
      {children}
    </AppLayout>
  );
};

export default SellerLayout;
